import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, MessageSquare } from 'lucide-react';

/**
 * Public SMS opt-in (2026-08-27). No login: this page is the consent proof
 * Twilio's toll-free verification cites, and a reviewer with no NADC account
 * has to be able to open it.
 *
 * Every element below is a carrier requirement, not decoration — the message
 * description, the frequency, the rates disclaimer, HELP/STOP, the Terms and
 * Privacy links, and an explicit submit. The consent checkbox MUST render
 * unchecked: `checked={consent}` with consent initialised false, and nothing
 * anywhere sets it on mount. A pre-ticked box fails review outright.
 *
 * The wording in the checkbox label is duplicated verbatim in
 * SmsOptInController::CONSENT_TEXT, which is what gets stamped into the audit
 * record. Change one, change both.
 */
export function SmsOptInPage() {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false); // MUST start false
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setState('sending');
    try {
      const r = await fetch('/api/v1/public/sms-opt-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          employee_number: employeeNumber,
          last_name: lastName,
          phone,
          consent,
        }),
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) {
        setError(
          body?.error?.message ??
            body?.message ??
            'That did not go through. Try again, or ask the office to add your number.'
        );
        setState('idle');
        return;
      }
      setFirstName(body?.data?.first_name ?? null);
      setState('done');
    } catch {
      setError('No connection — try again when you have signal.');
      setState('idle');
    }
  }

  const field =
    'mt-1 w-full min-h-touch rounded-lg border border-graphite-700 bg-graphite-950 px-3 py-2 text-graphite-100 placeholder-graphite-600 focus:border-safety-500 focus:outline-none focus:ring-1 focus:ring-safety-500';

  if (state === 'done') {
    return (
      <div data-industrial className="ui-ind-paint min-h-screen bg-graphite-950 px-4 py-10 text-graphite-100">
        <div className="mx-auto max-w-md text-center">
          <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-red-500">NADC · DemoOps</p>
          <CheckCircle2 className="mx-auto h-12 w-12 text-signal-500" aria-hidden />
          <h1 className="mt-4 text-xl font-bold">
            {firstName ? `You're signed up, ${firstName}.` : "You're signed up."}
          </h1>
          <p className="mt-2 text-sm text-graphite-400">
            We&rsquo;ll text your weekly schedule to the number you gave us. Reply <b>STOP</b> at any
            time to stop receiving them, or <b>HELP</b> for help.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-industrial className="ui-ind-paint min-h-screen bg-graphite-950 px-4 py-8 text-graphite-100">
      <div className="mx-auto max-w-md">
        <p className="mb-1 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-red-500">NADC · DemoOps</p>
        <h1 className="text-center text-xl font-bold">Get your schedule by text</h1>

        <div className="mt-5 flex items-start gap-3 rounded-xl border border-graphite-700 bg-graphite-900 p-4">
          <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-safety-500" aria-hidden />
          <p className="text-sm leading-relaxed text-graphite-300">
            Get your weekly work schedule by text. Messages list your assigned jobs, dates, start
            times, and meeting locations for the coming week, with a link to your full schedule.
            Message frequency varies, up to about <b>5 messages per month</b>.
          </p>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="employee_number" className="text-sm font-medium text-graphite-200">
              Employee number
            </label>
            <input
              id="employee_number"
              name="employee_number"
              required
              autoComplete="off"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              className={field}
              placeholder="From your paystub"
            />
          </div>

          <div>
            <label htmlFor="last_name" className="text-sm font-medium text-graphite-200">
              Last name
            </label>
            <input
              id="last_name"
              name="last_name"
              required
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={field}
            />
          </div>

          <div>
            <label htmlFor="phone" className="text-sm font-medium text-graphite-200">
              Mobile number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              required
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={field}
              placeholder="(248) 555-0134"
            />
            <p className="mt-1 text-xs text-graphite-500">
              Must be a mobile phone that can receive texts — a landline will not work.
            </p>
          </div>

          <label
            htmlFor="consent"
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-graphite-700 bg-graphite-900 p-4"
          >
            <input
              id="consent"
              name="consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-graphite-600 bg-graphite-950 text-safety-500 focus:ring-safety-500"
            />
            <span className="text-sm leading-relaxed text-graphite-300">
              I agree to receive automated work-schedule text messages from North American
              Dismantling Corp. at the mobile number provided. Consent is not a condition of
              employment. Message and data rates may apply. Message frequency varies. Reply{' '}
              <b>HELP</b> for help or <b>STOP</b> to unsubscribe. See our{' '}
              <Link to="/terms" className="text-safety-400 underline hover:text-safety-300">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-safety-400 underline hover:text-safety-300">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          {error && (
            <p role="alert" className="rounded-lg border border-danger-500/40 bg-danger-500/10 p-3 text-sm text-danger-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={state === 'sending' || !consent}
            className="flex min-h-touch w-full items-center justify-center gap-2 rounded-xl bg-safety-500 px-4 py-3 font-semibold text-white transition hover:bg-safety-400 disabled:cursor-not-allowed disabled:bg-graphite-700 disabled:text-graphite-500"
          >
            {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Yes, sign me up!
          </button>
        </form>

        <p className="mt-5 text-center text-xs leading-relaxed text-graphite-500">
          Message and data rates may apply. Reply STOP to unsubscribe, HELP for help.
          <br />
          <Link to="/terms" className="underline hover:text-graphite-400">Terms of Service</Link>
          {' · '}
          <Link to="/privacy" className="underline hover:text-graphite-400">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}

export default SmsOptInPage;
